import React from "react";

import _ from "lodash";

import useDragAndDropList, { DragAndDropListHook } from "commons/hooks/useDragAndDropList";
import useForm from "commons/hooks/useForm";

import { PathParamsWithId, ResponseWithId } from "commons/types/api/common";
import { FormHookType } from "commons/types/form";
import { OptionsType, ResponseType } from "commons/types/http";
import { ObjectPropertyType } from "commons/types/validation";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { DesktopMobileForm, ImagesBody, ImagesDetails } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import {
    calculateStatus,
    converterAttachmentToFileWithPayload,
    createAttachmentType,
} from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type OrderableImagesHookParams<T extends ImagesBody> = {
    createImage: (
        body: T,
        options?: OptionsType,
        headers?: {
            [x: string]: string;
        }
    ) => ResponseType<ResponseWithId>;
    updateImage: (
        pathParams: PathParamsWithId,
        body: T,
        options?: OptionsType,
        headers?: {
            [x: string]: string;
        }
    ) => ResponseType<null>;
    deleteImage: (
        pathParams: PathParamsWithId,
        options?: OptionsType,
        headers?: {
            [x: string]: string;
        }
    ) => ResponseType<null>;
    fieldId: string;
};

type ImagesForm = {
    id?: string;
    graphical_order: number;
    media: DesktopMobileForm;
    visible: boolean;
    toDelete: boolean;
};

type BodyWithImages = {
    images: Array<ImagesForm>;
};

export type OrderableImagesHook = {
    initializeOrderableImage: (images: Array<ImagesDetails>) => void;
    saveOrderableImages: (itemId: string) => Promise<unknown>;
    setImage: (file: AttachmentType, target: DesktopOrMobile, index: number) => void;
    deleteRow: (index: number) => void;
    addImage: () => void;
    dragAndDropHook: DragAndDropListHook<HTMLDivElement>;
    formHook: FormHookType<BodyWithImages>;
    visibleItemsLenght: number;
};

function useOrderableImages<T extends ImagesBody>({
    createImage,
    updateImage,
    deleteImage,
    fieldId,
}: OrderableImagesHookParams<T>): OrderableImagesHook {
    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                images: {
                    type: "objectArray",
                    of: {
                        type: "object",
                        validators: [],
                        properties: {
                            graphical_order: {
                                type: "simple",
                                validators: [],
                            },
                            media: notEmptyMedia,
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: BodyWithImages = React.useMemo(() => {
        return {
            images: [
                {
                    graphical_order: 10,
                    media: {
                        id: undefined,
                        desktop: createAttachmentType({ label: "desktop" }),
                        mobile: createAttachmentType({ label: "mobile" }),
                    },
                    visible: true,
                    toDelete: false,
                },
            ],
        };
    }, []);

    const formHook = useForm<BodyWithImages>(schema, initialValues, getExtraErrorsDescription);

    const { setForm, form } = formHook;

    const setImage = React.useCallback(
        (file: AttachmentType, target: DesktopOrMobile, index: number) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                if (
                    newForm.images[index] &&
                    newForm.images[index].media &&
                    newForm.images[index].media[target]
                ) {
                    if (file) {
                        newForm.images[index].media[target] = createAttachmentType({
                            filename: file.filename,
                            content: file.content,
                            contentType: file.contentType ?? "",
                            label: file.label,
                            size: file.size,
                            status: calculateStatus(file.filename, file.content),
                        });
                    } else {
                        newForm.images[index].media[target] = createAttachmentType({
                            label: "",
                        });
                    }
                }

                return newForm;
            });
        },
        [setForm]
    );

    const addImage = React.useCallback(() => {
        setForm((prevForm) => {
            const newForm = _.cloneDeep(prevForm);

            newForm.images.push({
                graphical_order: (newForm.images.length + 1) * 10,
                media: {
                    id: undefined,
                    desktop: createAttachmentType({
                        label: "Desktop",
                    }),
                    mobile: createAttachmentType({
                        label: "Mobile",
                    }),
                },
                visible: true,
                toDelete: false,
            });

            return newForm;
        });
    }, [setForm]);

    const deleteRow = React.useCallback(
        (index) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const finalForm: BodyWithImages = {
                    images: [],
                };

                // if the file is local I just remove the object, if there is an id so it's on the server I set it to delete
                // this to prevent issues with the validity of the form
                newForm.images.forEach((image, imageIndex) => {
                    if (imageIndex === index) {
                        if (image.id) {
                            finalForm.images.push({
                                ...newForm.images[index],
                                visible: false,
                                toDelete: true,
                            });
                        }
                    } else {
                        finalForm.images.push(image);
                    }
                });

                return finalForm;
            });
        },
        [setForm]
    );

    React.useEffect(() => {
        if (form.images.length === 0) {
            setForm(initialValues);
        }
    }, [setForm, initialValues, form.images.length]);

    const onUpdateList = React.useCallback(
        (newList) => {
            setForm((prevList) => {
                const tmpList = _.cloneDeep(prevList);

                tmpList.images = newList;

                tmpList.images.forEach((singleRow, index) => {
                    tmpList.images[index].graphical_order = (index + 1) * 10;
                });

                return tmpList;
            });
        },
        [setForm]
    );

    const dragAndDropHook = useDragAndDropList({ list: form.images, onUpdateList });

    const saveOrderableImages = React.useCallback(
        (itemId: string) => {
            const promises: Promise<unknown>[] = [];

            const newForm: BodyWithImages = _.cloneDeep(form);

            form.images.forEach((image, index) => {
                if (image.toDelete) {
                    if (image.id) {
                        const [apiCall] = deleteImage({ id: image.id });

                        apiCall.then(() => {
                            newForm.images[index].toDelete = false;
                        });

                        promises.push(apiCall);
                    }
                } else {
                    const body: ImagesBody = {
                        id: image.id,
                        graphical_order: image.graphical_order,
                        media: {
                            id: image.media.id,
                            desktop: converterAttachmentToFileWithPayload(image.media.desktop),
                            mobile: converterAttachmentToFileWithPayload(image.media.mobile),
                        },
                    };

                    if (image.id) {
                        const [apiCall] = updateImage({ id: image.id }, {
                            ...body,
                            [fieldId]: itemId,
                        } as T);

                        promises.push(apiCall);
                    } else {
                        const createBody = {
                            ...body,
                            [fieldId]: itemId,
                        } as T;

                        if (createBody.media.desktop && createBody.media.mobile) {
                            const [apiCall] = createImage(createBody);

                            apiCall.then((response) => {
                                newForm.images[index].id = response.id;
                            });

                            promises.push(apiCall);
                        }
                    }
                }
            });

            setForm(newForm);

            return Promise.all(promises);
        },
        [setForm, createImage, updateImage, deleteImage, fieldId, form]
    );

    const visibleItemsLenght = React.useMemo(() => {
        return form.images.reduce((counter, image) => counter + (image.visible ? 1 : 0), 0);
    }, [form]);

    const initializeOrderableImage = React.useCallback(
        (images: Array<ImagesDetails>) => {
            const newForm: BodyWithImages = {
                images: [],
            };

            images.forEach((image) => {
                newForm.images.push({
                    id: image.id,
                    graphical_order: image.graphical_order,
                    media: {
                        id: image.media.id,
                        desktop: createAttachmentType({
                            id: image.media.desktop.id,
                            label: "Desktop",
                            filename: image.media.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: image.media.mobile.id,
                            label: "Mobile",
                            filename: image.media.mobile.filename,
                            status: 2,
                        }),
                    },
                    visible: true,
                    toDelete: false,
                });
            });

            setForm(newForm);
        },
        [setForm]
    );

    return React.useMemo(
        (): OrderableImagesHook => ({
            initializeOrderableImage,
            saveOrderableImages,
            deleteRow,
            setImage,
            addImage,
            dragAndDropHook,
            formHook,
            visibleItemsLenght,
        }),
        [
            initializeOrderableImage,
            saveOrderableImages,
            setImage,
            addImage,
            deleteRow,
            dragAndDropHook,
            formHook,
            visibleItemsLenght,
        ]
    );
}

export default useOrderableImages;
